import { useEffect,useRef } from 'react'
import { useFrame,useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { arenas,fighters,type ArenaDef } from './content'
import { WORLD_COMPOSITIONS } from './generatedWorldExpansion'
import { SUPREME_GAME_ENGINES } from './SupremeGameEngines'
import { useFightStore } from './store'

export const AUTONOMY_ENGINE_IDS=[
 'life','state-integrity-sentinel','scene-graph-sentinel','animation-rig-sentinel',
 'closure','blender-forge','release-guardian',
] as const

type IntegrityIssue={code:string;severity:'repair'|'fatal';detail:string}
export type FightIntegrityAssessment={ok:boolean;issues:IntegrityIssue[]}

const finite=(value:number)=>Number.isFinite(value)
const safeIndex=(value:number,count:number)=>Number.isSafeInteger(value)&&value>=0&&value<count
const finiteCombatant=(actor:{x:number;z:number;health:number;maxHealth:number})=>
 finite(actor.x)&&finite(actor.z)&&finite(actor.health)&&finite(actor.maxHealth)&&actor.maxHealth>0

export function assessFightIntegrity(state:ReturnType<typeof useFightStore.getState>):FightIntegrityAssessment{
 const issues:IntegrityIssue[]=[]
 if(!safeIndex(state.playerIndex,fighters.length))issues.push({code:'player-index',severity:'fatal',detail:String(state.playerIndex)})
 if(!safeIndex(state.enemyIndex,fighters.length))issues.push({code:'enemy-index',severity:'fatal',detail:String(state.enemyIndex)})
 if(!safeIndex(state.arenaIndex,arenas.length))issues.push({code:'arena-index',severity:'fatal',detail:String(state.arenaIndex)})
 if(!finiteCombatant(state.player))issues.push({code:'player-state',severity:'fatal',detail:'non-finite or invalid maxHealth'})
 if(!finiteCombatant(state.enemy))issues.push({code:'enemy-state',severity:'fatal',detail:'non-finite or invalid maxHealth'})
 if(!finite(state.timer)||state.timer<0)issues.push({code:'timer',severity:'repair',detail:String(state.timer)})
 if(finite(state.player.health)&&finite(state.player.maxHealth)&&(state.player.health<0||state.player.health>state.player.maxHealth))issues.push({code:'player-health-range',severity:'repair',detail:String(state.player.health)})
 if(finite(state.enemy.health)&&finite(state.enemy.maxHealth)&&(state.enemy.health<0||state.enemy.health>state.enemy.maxHealth))issues.push({code:'enemy-health-range',severity:'repair',detail:String(state.enemy.health)})
 return{ok:issues.length===0,issues}
}

function normalizeIndex(value:number,count:number,fallback:number){return safeIndex(value,count)?value:Math.max(0,Math.min(count-1,fallback))}
function StateIntegritySentinel(){
 const timer=useRef(0),repairs=useRef(0),recoveries=useRef(0)
 const {scene}=useThree()
 useFrame((_,dt)=>{
  timer.current+=Math.max(0,dt);if(timer.current<.25)return;timer.current=0
  const s=useFightStore.getState(),assessment=assessFightIntegrity(s)
  if(assessment.ok){
   scene.userData.inyronStateSentinel={ok:true,repairs:repairs.current,recoveries:recoveries.current,issues:[],at:performance.now()}
   return
  }
  const playerIndex=normalizeIndex(s.playerIndex,fighters.length,0)
  const enemyIndex=normalizeIndex(s.enemyIndex,fighters.length,Math.min(1,fighters.length-1))
  const arenaIndex=normalizeIndex(s.arenaIndex,arenas.length,0)
  const fatal=assessment.issues.some(issue=>issue.severity==='fatal')
  if(fatal){
   useFightStore.setState({playerIndex,enemyIndex,arenaIndex})
   useFightStore.getState().resetMatch()
   recoveries.current++
  }else{
   const player={...s.player,health:THREE.MathUtils.clamp(finite(s.player.health)?s.player.health:0,0,Math.max(1,s.player.maxHealth))}
   const enemy={...s.enemy,health:THREE.MathUtils.clamp(finite(s.enemy.health)?s.enemy.health:0,0,Math.max(1,s.enemy.maxHealth))}
   useFightStore.setState({player,enemy,timer:finite(s.timer)?Math.max(0,s.timer):0})
   repairs.current++
  }
  scene.userData.inyronStateSentinel={ok:false,repairs:repairs.current,recoveries:recoveries.current,issues:assessment.issues,at:performance.now()}
 })
 return null
}

function repairVector(v:THREE.Vector3,identity=0){
 let changed=0
 if(!finite(v.x)){v.x=identity;changed++}
 if(!finite(v.y)){v.y=identity;changed++}
 if(!finite(v.z)){v.z=identity;changed++}
 return changed
}
function repairEuler(v:THREE.Euler){
 let changed=0
 if(!finite(v.x)){v.x=0;changed++}
 if(!finite(v.y)){v.y=0;changed++}
 if(!finite(v.z)){v.z=0;changed++}
 return changed
}
function SceneGraphSentinel(){
 const {scene}=useThree(),timer=useRef(0),repairs=useRef(0),scans=useRef(0)
 useFrame((_,dt)=>{
  timer.current+=Math.max(0,dt);if(timer.current<4)return;timer.current=0;scans.current++
  let repairedThisScan=0,objects=0
  scene.traverse(object=>{
   objects++
   // Bone transforms belong exclusively to AnimationRigSentinel + the mixer.
   // Scene-level repair touching them creates a second writer and can deform skin/attachments.
   if((object as THREE.Bone).isBone)return
   repairedThisScan+=repairVector(object.position,0)
   repairedThisScan+=repairEuler(object.rotation)
   // Finite zero/tiny scales are valid authored visibility/attachment states; preserve them.
   const badScale=!finite(object.scale.x)||!finite(object.scale.y)||!finite(object.scale.z)
   if(badScale){object.scale.set(1,1,1);repairedThisScan++}
   if(!finite(object.quaternion.x)||!finite(object.quaternion.y)||!finite(object.quaternion.z)||!finite(object.quaternion.w)||object.quaternion.lengthSq()<1e-8){
    object.quaternion.identity();repairedThisScan++
   }else object.quaternion.normalize()
  })
  repairs.current+=repairedThisScan
  scene.userData.inyronSceneSentinel={ok:repairedThisScan===0,objects,scans:scans.current,repairs:repairs.current,lastRepairs:repairedThisScan,at:performance.now()}
 })
 return null
}

function AnimationRigSentinel(){
 const {scene}=useThree(),timer=useRef(0),repairs=useRef(0),scans=useRef(0)
 useFrame((_,dt)=>{
  timer.current+=Math.max(0,dt);if(timer.current<2)return;timer.current=0;scans.current++
  let repairedThisScan=0,skeletons=0,bones=0,suspectFiniteScales=0
  scene.traverse(object=>{
   if(!(object instanceof THREE.SkinnedMesh)||!object.skeleton)return
   skeletons++
   for(const bone of object.skeleton.bones){
    bones++
    repairedThisScan+=repairVector(bone.position,0)
    if(!finite(bone.quaternion.x)||!finite(bone.quaternion.y)||!finite(bone.quaternion.z)||!finite(bone.quaternion.w)||bone.quaternion.lengthSq()<1e-8){
      bone.quaternion.identity();repairedThisScan++
    }else bone.quaternion.normalize()
    // Never clamp finite authored bone scale at runtime: clips may legitimately drive
    // accessories/cloth visibility. Repair only non-finite corruption and report extremes.
    if(!finite(bone.scale.x)||!finite(bone.scale.y)||!finite(bone.scale.z)){bone.scale.set(1,1,1);repairedThisScan++}
    else if(Math.max(Math.abs(bone.scale.x),Math.abs(bone.scale.y),Math.abs(bone.scale.z))>50)suspectFiniteScales++
   }
   try{object.skeleton.update()}catch{}
  })
  repairs.current+=repairedThisScan
  scene.userData.inyronRigSentinel={ok:repairedThisScan===0,skeletons,bones,suspectFiniteScales,scans:scans.current,repairs:repairs.current,lastRepairs:repairedThisScan,at:performance.now()}
 })
 return null
}
export type LifeSnapshot={
 version:1;arenaId:string;status:string;matchSerial:number;heartbeat:number;
 playerHealthRatio:number;enemyHealthRatio:number;worldComposition:boolean;
 supremeEngineCount:number;protection:{state:boolean;scene:boolean;rig:boolean};
 continuityScore:number;at:number
}
export function createLifeSnapshot(arena:ArenaDef,state:ReturnType<typeof useFightStore.getState>,userData:Record<string,unknown>,heartbeat:number):LifeSnapshot{
 const ratio=(health:number,maxHealth:number)=>THREE.MathUtils.clamp(maxHealth>0&&finite(health)&&finite(maxHealth)?health/maxHealth:0,0,1)
 const stateHealth=userData.inyronStateSentinel as {ok?:boolean}|undefined
 const sceneHealth=userData.inyronSceneSentinel as {ok?:boolean;repairs?:number}|undefined
 const rigHealth=userData.inyronRigSentinel as {ok?:boolean;repairs?:number}|undefined
 const repairPressure=Math.min(1,((sceneHealth?.repairs??0)+(rigHealth?.repairs??0))/100)
 return{
  version:1,arenaId:arena.id,status:state.status,matchSerial:state.matchSerial,heartbeat,
  playerHealthRatio:ratio(state.player.health,state.player.maxHealth),enemyHealthRatio:ratio(state.enemy.health,state.enemy.maxHealth),
  worldComposition:Boolean(WORLD_COMPOSITIONS[arena.id]),supremeEngineCount:SUPREME_GAME_ENGINES.length,
  protection:{state:stateHealth?.ok!==false,scene:sceneHealth?.ok!==false,rig:rigHealth?.ok!==false},
  continuityScore:Math.max(0,1-repairPressure),at:performance.now(),
 }
}

function LifeEngine({arena}:{arena:ArenaDef}){
 const {scene}=useThree(),timer=useRef(0),heartbeat=useRef(0)
 useEffect(()=>{
  scene.userData.inyronLife={status:'booting',arenaId:arena.id,heartbeat:heartbeat.current,at:performance.now()}
 },[arena,scene])
 useFrame((_,dt)=>{
  timer.current+=Math.max(0,dt);if(timer.current<1)return;timer.current=0;heartbeat.current++
  const snapshot=createLifeSnapshot(arena,useFightStore.getState(),scene.userData,heartbeat.current)
  scene.userData.inyronLife=snapshot
  if(typeof window!=='undefined')window.dispatchEvent(new CustomEvent('inyron-life-pulse',{detail:snapshot}))
 })
 return null
}

export function AutonomyEngines({arena}:{arena:ArenaDef}){
 return <>
  <StateIntegritySentinel/>
  <SceneGraphSentinel/>
  <AnimationRigSentinel/>
  <LifeEngine arena={arena}/>
 </>
}
